import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Accordion } from '../components/ui/accordion';
import { ArrowLeft, UserPlus, Loader2 } from 'lucide-react';

// Import refactored sub-components
import {
  BasicInfoSection,
  AdminInfoSection,
  AgentDetailsSection,
  ShopUsersSection,
  SubscriptionsSection,
  PaymentHistorySection,
  ResetPasswordDialog,
  AddUserDialog,
  AddAdminDialog,
} from '../components/shop-details';

const ShopDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEdit } = useAuth();
  
  const [shop, setShop] = useState(null);
  const [shopUsers, setShopUsers] = useState({ adminUsers: [], otherUsers: [] });
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dialog states
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [showAddAdminDialog, setShowAddAdminDialog] = useState(false);

  useEffect(() => {
    fetchShopDetails();
  }, [id]);

  const fetchShopDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.getShopDetails(id);
      
      if (response.success) {
        setShop(response.data.shop);
        
        // Separate admin users from other users
        const allUsers = response.data.relations?.shopUsers || {};
        const adminUser = allUsers.adminUser;
        const otherUsers = allUsers.otherUsers || [];
        
        // Collect all admin users (adminUser + any shopAdmin from otherUsers)
        const adminUsers = [];
        if (adminUser) {
          adminUsers.push(adminUser);
        }
        const nonAdminUsers = otherUsers.filter(u => {
          if (u.role === 'shopAdmin') {
            adminUsers.push(u);
            return false;
          }
          return true;
        });
        
        setShopUsers({
          adminUsers,
          otherUsers: nonAdminUsers,
        });
        
        // Use subscriptions from shop object (has full details including startDate, nextRenewalDate)
        setSubscriptions(response.data.shop?.subscriptions || []);
      } else {
        setError(response.message || 'Failed to fetch shop details');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch shop details');
    } finally {
      setLoading(false);
    }
  };

  // Handler for basic info local update
  const handleBasicInfoUpdate = (updatedInfo) => {
    setShop(prev => ({ ...prev, ...updatedInfo }));
  };

  // Handler for agent details update
  const handleAgentUpdate = (updates) => {
    setShop(prev => ({ ...prev, ...updates }));
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-[#2563EB] animate-spin mx-auto" />
          <p className="text-[#94A3B8] mt-2">Loading shop details...</p>
        </div>
      </div>
    );
  }

  if (error || !shop) {
    return (
      <div className="p-6 md:p-8" data-testid="shop-not-found">
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-[#E2E8F0] mb-2">
            {error || 'Shop not found'}
          </h2>
          <p className="text-[#94A3B8] mb-4">The shop you're looking for doesn't exist or could not be loaded.</p>
          <Button 
            onClick={() => navigate('/shops')}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
          >
            Back to Shops
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="shop-details-page">
      {/* Header */}
      <div className="mb-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/shops')}
          className="text-[#94A3B8] hover:text-[#E2E8F0] hover:bg-[#334155]/50 mb-4 -ml-4"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Shops
        </Button>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#E2E8F0] tracking-tight mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {shop.shopName}
            </h1>
            <p className="text-[#94A3B8]">{shop.address}, {shop.city}, {shop.state}</p>
          </div>
          {canEdit() && (
            <Button
              onClick={() => navigate(`/shops/${id}/admin/new`)}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
              data-testid="register-admin-button"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Register Admin
            </Button>
          )}
        </div>
      </div>

      {/* Accordion Sections */}
      <Accordion type="multiple" defaultValue={['basic', 'admin', 'agent', 'users', 'subscriptions', 'payments']} className="space-y-4">
        <BasicInfoSection 
          shop={shop} 
          canEdit={canEdit()} 
          onUpdate={handleBasicInfoUpdate} 
        />
        
        <AdminInfoSection 
          adminUsers={shopUsers.adminUsers}
          canEdit={canEdit()}
          onAddAdmin={() => setShowAddAdminDialog(true)}
          onResetPassword={() => setShowResetPasswordDialog(true)}
        />
        
        <AgentDetailsSection 
          shop={shop}
          shopId={id}
          canEdit={canEdit()}
          onUpdate={handleAgentUpdate}
        />
        
        <ShopUsersSection 
          users={shopUsers.otherUsers}
          canEdit={canEdit()}
          onAddUser={() => setShowAddUserDialog(true)}
        />
        
        <SubscriptionsSection 
          subscriptions={subscriptions}
        />
        
        <PaymentHistorySection 
          shopId={id}
        />
      </Accordion>

      {/* Dialogs */}
      <ResetPasswordDialog
        open={showResetPasswordDialog}
        onOpenChange={setShowResetPasswordDialog}
        shopId={id}
        adminEmail={shopUsers.adminUsers?.[0]?.email}
      />

      <AddUserDialog
        open={showAddUserDialog}
        onOpenChange={setShowAddUserDialog}
        shopId={id}
        shopName={shop.shopName}
        onSuccess={fetchShopDetails}
      />

      <AddAdminDialog
        open={showAddAdminDialog}
        onOpenChange={setShowAddAdminDialog}
        shopId={id}
        shopName={shop.shopName}
        onSuccess={fetchShopDetails}
      />
    </div>
  );
};

export default ShopDetails;
